import { checkCollision, getVehiclePos, type Point2D } from './collision'
import { COUPLING_IMPACT_FACTOR, EPSILON } from './constants'
import { loadProbe, moveVehicleBy } from './movement'
import { fillAllVehicles, getVehicleById } from './queries'
import type { Network, Vehicle } from './types'

const TOUCH_SIDE_PROBE_DISTANCE = 0.01

const touchProbe: Vehicle = { id: '', type: 'wagon', edgeId: '', direction: 1, length: 0, t: 0 }
const touchPosSelf: Point2D = { x: 0, z: 0 }
const touchPosTarget: Point2D = { x: 0, z: 0 }
const touchPosProbe: Point2D = { x: 0, z: 0 }
const couplingVehiclesScratch: Vehicle[] = []

/** Links two vehicles on the given sides. Every link mutation must go through here or `unlinkVehicles`. */
export function linkVehicles(
  network: Network,
  vA: Vehicle,
  sideA: 'linkedFront' | 'linkedBack',
  vB: Vehicle,
  sideB: 'linkedFront' | 'linkedBack',
) {
  vA[sideA] = vB.id
  vB[sideB] = vA.id
  network.linkVersion++
  network.consistDirty = true
}

/** Breaks the link between two vehicles, whichever sides it is on. */
export function unlinkVehicles(network: Network, v1: Vehicle, v2: Vehicle) {
  if (v1.linkedFront === v2.id) v1.linkedFront = undefined
  else if (v1.linkedBack === v2.id) v1.linkedBack = undefined

  if (v2.linkedFront === v1.id) v2.linkedFront = undefined
  else if (v2.linkedBack === v1.id) v2.linkedBack = undefined

  network.linkVersion++
  network.consistDirty = true
}

/**
 * Determines which side (front or back) of vehicle `v` is physically touching `target`.
 *
 * Virtually moves `v` forward by a tiny step. If the distance to `target` decreases,
 * the front is the closer side; otherwise it is the back. When the front is blocked
 * (dead end, switch set against it) the probe goes backward instead and the test is inverted.
 */
export function getTouchingSide(
  network: Network,
  v: Vehicle,
  target: Vehicle,
): 'linkedFront' | 'linkedBack' {
  const p1 = getVehiclePos(network, v, undefined, touchPosSelf)
  const p2 = getVehiclePos(network, target, undefined, touchPosTarget)
  if (!p1 || !p2) return 'linkedFront'

  const probe = loadProbe(v, touchProbe)
  let step = TOUCH_SIDE_PROBE_DISTANCE
  if (Math.abs(moveVehicleBy(network, probe, step)) <= EPSILON) {
    loadProbe(v, probe)
    step = -TOUCH_SIDE_PROBE_DISTANCE
    if (Math.abs(moveVehicleBy(network, probe, step)) <= EPSILON) return 'linkedBack'
  }

  const pProbe = getVehiclePos(network, probe, undefined, touchPosProbe) ?? p1
  const distNow = (p1.x - p2.x) ** 2 + (p1.z - p2.z) ** 2
  const distProbe = (pProbe.x - p2.x) ** 2 + (pProbe.z - p2.z) ** 2
  const probeCloser = distProbe < distNow

  return probeCloser === step > 0 ? 'linkedFront' : 'linkedBack'
}

function buildUncoupledCollisionKey(
  vA: Vehicle,
  portA: 'linkedFront' | 'linkedBack',
  vB: Vehicle,
  portB: 'linkedFront' | 'linkedBack',
): string {
  return [`${vA.id}:${portA}:${vA.direction}`, `${vB.id}:${portB}:${vB.direction}`].sort().join('|')
}

/** Ids of the pair that was just uncoupled and is still touching, if any. */
export function getRecentUncoupledPairIds(network: Network): [string, string] | null {
  const collisionKey = network.locomotive?.lastUncoupledCollisionKey
  if (!collisionKey) return null

  const separator = collisionKey.indexOf('|')
  const idA = collisionKey.slice(0, collisionKey.indexOf(':'))
  const idB = collisionKey.slice(separator + 1, collisionKey.indexOf(':', separator))
  return idA && idB ? [idA, idB] : null
}

export function clearRecentUncoupledCollisionKey(network: Network) {
  if (network.locomotive) {
    network.locomotive.lastUncoupledCollisionKey = undefined
    network.locomotive.uncoupleDirectionLock = undefined
  }
}

export function syncRecentUncoupledCollisionKey(network: Network) {
  const loco = network.locomotive
  const collisionKey = loco?.lastUncoupledCollisionKey
  if (!loco || !collisionKey) return

  const [entryA, entryB] = collisionKey.split('|')
  if (!entryA || !entryB) {
    clearRecentUncoupledCollisionKey(network)
    return
  }

  const [idA, sideA, directionA] = entryA.split(':')
  const [idB, sideB, directionB] = entryB.split(':')
  if (
    (sideA !== 'linkedFront' && sideA !== 'linkedBack') ||
    (sideB !== 'linkedFront' && sideB !== 'linkedBack') ||
    (directionA !== '1' && directionA !== '-1') ||
    (directionB !== '1' && directionB !== '-1')
  ) {
    clearRecentUncoupledCollisionKey(network)
    return
  }

  const vehicleA = getVehicleById(network, idA)
  const vehicleB = getVehicleById(network, idB)
  if (
    !vehicleA ||
    !vehicleB ||
    `${vehicleA.direction}` !== directionA ||
    `${vehicleB.direction}` !== directionB ||
    !checkCollision(network, vehicleA, vehicleB)
  ) {
    clearRecentUncoupledCollisionKey(network)
    return
  }

  const currentKey = buildUncoupledCollisionKey(
    vehicleA,
    getTouchingSide(network, vehicleA, vehicleB),
    vehicleB,
    getTouchingSide(network, vehicleB, vehicleA),
  )
  if (currentKey !== collisionKey) clearRecentUncoupledCollisionKey(network)
}

/** Callers must run `syncRecentUncoupledCollisionKey` once per pass before querying. */
export function isRecentUncoupledCollision(
  network: Network,
  vA: Vehicle,
  portA: 'linkedFront' | 'linkedBack',
  vB: Vehicle,
  portB: 'linkedFront' | 'linkedBack',
): boolean {
  const loco = network.locomotive
  const collisionKey = loco?.lastUncoupledCollisionKey
  if (!loco || !collisionKey) return false

  return buildUncoupledCollisionKey(vA, portA, vB, portB) === collisionKey
}

/**
 * Scans all vehicle pairs and couples any that are touching with free link slots.
 * Skips pairs already in the same consist (both in `moves`).
 * Blocks only the exact post-uncouple collision signature that was just separated.
 */
export function runCouplingPass(network: Network, moves: Map<Vehicle, number>) {
  const loco = network.locomotive
  if (!loco) return

  syncRecentUncoupledCollisionKey(network)

  const allVehicles = fillAllVehicles(network, couplingVehiclesScratch)

  // No standalone wagons — nothing can couple this frame.
  if (moves.size === allVehicles.length) return

  for (let i = 0; i < allVehicles.length; i++) {
    for (let j = i + 1; j < allVehicles.length; j++) {
      const vA = allVehicles[i]
      const vB = allVehicles[j]

      if (moves.has(vA) && moves.has(vB)) continue
      if (!moves.has(vA) && !moves.has(vB)) continue

      if (!checkCollision(network, vA, vB)) continue

      const portA = getTouchingSide(network, vA, vB)
      const portB = getTouchingSide(network, vB, vA)
      if (isRecentUncoupledCollision(network, vA, portA, vB, portB)) continue

      if (!vA[portA] && !vB[portB]) {
        linkVehicles(network, vA, portA, vB, portB)
        loco.currentVelocity *= COUPLING_IMPACT_FACTOR
        loco.lastUncoupledCollisionKey = undefined
        loco.uncoupleDirectionLock = undefined
        network.couplingCount++
      }
    }
  }
}
